import { useCallback, useEffect, useMemo, useState } from "react";
import {
  alertService,
  getLockBUPaymentModes,
  getPaymentMethods,
  getRetentionMethods,
  loaderService,
  GetSettingsLabels,
  invalidateFavoriteDestinationsCache,
} from "../services";
import { useLocation, useNavigate } from "react-router-dom";
import { navigation } from "../utils/NavigationLists";
import { invalidate } from "../services/requestCache";
import SidebarStatic from "../components/SidebarStatic";
import SidebarDynamic from "../components/SidebarDynamic";
import { UserInterface } from "../interfaces/UserInterface";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  clearShipmentCreate,
  login,
  logout,
  setPaymentMethods,
  setRetentionMethods,
  setUserLocation,
} from "../store/slices";
import {
  CreateOrUpdateSession,
  logoutSession,
} from "../services/authetication";
import { BusinessUnitInterface } from "../interfaces";

const Layout = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState<number>(0);
  const { pathname: path } = useLocation();
  const dispatch = useAppDispatch();

  const userLogged = useAppSelector((state) => state.user.user);
  const businessUnit = useAppSelector((state) => state.user.businessUnit);
  const userSession = useAppSelector((state) => state.user.userSession);
  // T8: the whole `inmutable` slice was subscribed here to read one string.
  // That re-rendered Layout, and with it both sidebars, on each of the 17
  // setInmutable* dispatches of startup.
  const applicationCode = useAppSelector(
    (state) => state.inmutable.appData.applicationCode
  );

  useEffect(() => {
    let canceled = false;
    const refreshLabelConfig = async () => {
      if (businessUnit?.code) {
        try {
          const currentLabelFormat = await GetSettingsLabels(businessUnit.code);
          // Store switch or logout while the request was open: the closure's
          // businessUnit is the old one and must not be written back to Redux.
          if (canceled) return;
          if (businessUnit.labelFormat !== currentLabelFormat) {
            const updatedBU = { ...businessUnit, labelFormat: currentLabelFormat };
            dispatch(setUserLocation(updatedBU));
          }
        } catch (e) {
          console.error("No se pudo sincronizar config de etiqueta", e);
        }
      }
    };
    refreshLabelConfig();
    return () => {
      canceled = true;
    };
    // Kept on purpose (see getBUConfiguration in businessUnitServices): this
    // is how a label format saved on another terminal of the same store
    // reaches this one, and the LOCKS cache makes it cost at most one request
    // per five minutes. Only the store code decides whether to run; the object
    // itself would refire on the very dispatch this effect issues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, businessUnit?.code]);

  const filteredNavigation = useMemo(() => {
    return navigation.filter((nav) => {
      return (
        (!nav.rolesRequired ||
          (!!userLogged && nav.rolesRequired?.includes(userLogged.roleName))) &&
        (!nav.filter || nav.filter(userLogged ?? undefined, businessUnit))
      );
    });
  }, [businessUnit, userLogged]);

  useEffect(() => {
    const section = "/" + path.split("/")[1];
    const index = navigation.findIndex((item) => item.href === section);

    setCurrentItemIndex(index);
  }, [path]);

  // useCallback on both handlers: they are passed to SidebarStatic and
  // SidebarDynamic, which T6 memoises. An inline identity would make that memo
  // a no-op, and Layout re-renders on every navigation.
  const handleLogout = useCallback(async () => {
    if (!userLogged || !userSession) return;
    const response = await logoutSession(
      userLogged.userID,
      userSession.sessionID
    );
    // Outside the conditional on purpose: logoutSession resolves didError when
    // the token is already dead, which is the commonest way to reach a logout.
    // Leaving a warm cache behind is worse than clearing one we did not need to.
    invalidate();
    // A separate cache, and it has to be cleared too: tellers share a PC, and
    // the next session must never see the previous one's consignee books,
    // which are customer PII.
    invalidateFavoriteDestinationsCache();
    if (!response.didError) dispatch(logout());
  }, [userLogged, userSession, dispatch]);

  const handleBusinessUnitChange = useCallback(async (
    user: UserInterface,
    businessUnit: BusinessUnitInterface
  ) => {
    // The store changes without reloading the page, so anything cached for the
    // previous store has to go: its configuration, locked services and payment
    // modes are all keyed by BU code.
    invalidate();
    loaderService.start();
    // T9: four independent calls, one wave. See Login.saveSession.
    const [
      userSessionResponse,
      paymentMethodsResponse,
      retentionMethodsResponse,
      paymentModes,
    ] = await Promise.all([
      CreateOrUpdateSession(user.userID, applicationCode, businessUnit.code),
      getPaymentMethods(businessUnit.code, user.userLogin),
      getRetentionMethods(businessUnit.code, user.userLogin),
      getLockBUPaymentModes(businessUnit.code),
    ]);

    loaderService.stop();
    if (
      !userSessionResponse.didError &&
      !!userSessionResponse.model &&
      !paymentModes.didError &&
      !!paymentModes.model
    ) {
      let userSession = userSessionResponse.model;
      // The procedure does NOT scope the book by the session's business unit
      // (verified 2026-09-12: its only BusinessUnit references are LEFT JOINs
      // for display names), so this is insurance, not correctness — it costs
      // one refetch on a switch tellers make about once a day, and it is what
      // stops a stale book if the procedure ever gains a BU predicate.
      invalidateFavoriteDestinationsCache();
      dispatch(clearShipmentCreate());
      dispatch(login({ user: user, session: userSession }));
      dispatch(setUserLocation(businessUnit));
      dispatch(setPaymentMethods(paymentMethodsResponse));
      dispatch(setRetentionMethods(retentionMethodsResponse));
      navigate("/dashboard");
    } else {
      alertService.error(userSessionResponse.errorMessage);
    }
  }, [dispatch, navigate, applicationCode]);

  return (
    <>
      <SidebarDynamic
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        navigationItems={filteredNavigation}
        currentItemIndex={currentItemIndex}
        setCurrentItemIndex={setCurrentItemIndex}
        handleLogout={handleLogout}
        handleBusinessUnitChange={handleBusinessUnitChange}
      />
      <SidebarStatic
        navigationItems={filteredNavigation}
        currentItemIndex={currentItemIndex}
        setCurrentItemIndex={setCurrentItemIndex}
        handleLogout={handleLogout}
        handleBusinessUnitChange={handleBusinessUnitChange}
      />
    </>
  );
};

export default Layout;
